import type { IconName } from '../components/ui/Icon';
import type { ModalKind } from '../stores/modal.store';
import { ModuleKey } from '@masamune/shared';
import { isFrozen } from './frozen';

/**
 * FASE 5 — Fonte unica delle creazioni ("+ Nuovo").
 *
 * Prima questa lista esisteva DUE volte: nei `groups` del NewMenu in Topbar e
 * in `QUICK_ACTIONS` della CommandPalette. Qui c'è la sorgente condivisa: la
 * Topbar costruisce i suoi gruppi da `NEW_ACTIONS`, e la palette le sue azioni
 * rapide di creazione dalla STESSA lista (ora gated sui permessi).
 *
 * NON cambia il look: le voci e l'ordine ricalcano esattamente il NewMenu di
 * riferimento (Operativo / Documenti / Back-office). I `paletteColor` riprendono
 * i colori già usati nella palette per le creazioni preesistenti.
 */

/** Raggruppamento mostrato nel NewMenu della Topbar. */
export type NewActionGroup = 'Operativo' | 'Documenti' | 'Back-office';

export interface NewAction {
  /** Chiave stabile (usata anche come id `qa-*` nella palette). */
  key: string;
  /** Titolo voce (NewMenu) / label (palette). */
  title: string;
  /** Sottotitolo descrittivo. */
  sub: string;
  /** Icona NewMenu. */
  icon: IconName;
  /** Modale da aprire al click. */
  modal: Exclude<ModalKind, null>;
  /**
   * Moduli che abilitano la voce: visibile se l'utente ha WRITE su ALMENO uno
   * di essi (replica l'OR del NewMenu, es. Progetto = PRODUCT|MARKETING|DESIGN).
   */
  module: ModuleKey[];
  /** Gruppo nel NewMenu della Topbar. */
  group: NewActionGroup;
  /** Colore del cerchio icona nella palette (var(--xxx)), look preesistente. */
  paletteColor: string;
  /** Label mostrata nella palette ("Nuovo cliente", …): wording preesistente. */
  paletteLabel: string;
  /** Sottotitolo mostrato nella palette: wording preesistente. */
  paletteSub: string;
}

/**
 * Set di riferimento = le voci attuali del NewMenu della Topbar, nello stesso
 * ordine. La palette ne mostra il sottoinsieme con permesso.
 */
export const NEW_ACTIONS: NewAction[] = [
  // ── Operativo ──
  {
    key: 'new-project',
    title: 'Progetto',
    sub: 'Quattro domande',
    icon: 'cube',
    modal: 'newProject',
    module: [ModuleKey.PRODUCT, ModuleKey.MARKETING, ModuleKey.DESIGN],
    group: 'Operativo',
    paletteColor: 'var(--brand-product)',
    paletteLabel: 'Nuovo progetto',
    paletteSub: 'Cliente, nome, cosa si vende, consegna',
  },
  {
    key: 'new-client',
    title: 'Cliente',
    sub: 'Anagrafica CRM',
    icon: 'users',
    modal: 'newClient',
    module: [ModuleKey.CRM],
    group: 'Operativo',
    paletteColor: 'var(--brand-marketing)',
    paletteLabel: 'Nuovo cliente',
    paletteSub: 'Aggiungi un\'anagrafica al CRM',
  },
  {
    key: 'new-deal',
    title: 'Trattativa',
    sub: 'Voucher, rinnovo, estensione',
    icon: 'handshake',
    modal: 'newDeal',
    module: [ModuleKey.TRATTATIVE],
    group: 'Operativo',
    paletteColor: 'var(--accent)',
    paletteLabel: 'Nuova trattativa',
    paletteSub: 'Una pratica da portare avanti',
  },
  {
    key: 'new-task',
    title: 'Task',
    sub: 'Cosa fare, chi, entro quando',
    icon: 'tasks',
    modal: 'newTask',
    module: [ModuleKey.TASKS],
    group: 'Operativo',
    paletteColor: 'var(--accent)',
    paletteLabel: 'Nuovo task',
    paletteSub: 'Anche senza progetto',
  },
  // ── Documenti ──
  {
    key: 'new-quote',
    title: 'Preventivo',
    sub: 'Proposta firmabile (eIDAS)',
    icon: 'file',
    modal: 'newQuote',
    module: [ModuleKey.FATTURAZIONE],
    group: 'Documenti',
    paletteColor: 'var(--info)',
    paletteLabel: 'Nuovo preventivo',
    paletteSub: 'Crea una proposta firmabile',
  },
  {
    key: 'new-invoice',
    title: 'Fattura',
    sub: 'Documento fiscale numerato',
    icon: 'invoice',
    modal: 'newInvoice',
    module: [ModuleKey.FATTURAZIONE],
    group: 'Documenti',
    paletteColor: 'var(--ok)',
    paletteLabel: 'Nuova fattura',
    paletteSub: 'Emessa via FattureInCloud',
  },
  // ── Back-office ──
  {
    key: 'new-cash-entry',
    title: 'Contanti',
    sub: 'Incasso o uscita in cassa',
    icon: 'banknote',
    modal: 'newCashEntry',
    module: [ModuleKey.AMMINISTRAZIONE],
    group: 'Back-office',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuovo movimento in contanti',
    paletteSub: 'Incasso o uscita in cassa',
  },
  {
    key: 'new-tax',
    title: 'Scadenza fiscale',
    sub: 'F24, IVA, INPS, rate',
    icon: 'calendarClock',
    modal: 'newTax',
    module: [ModuleKey.AMMINISTRAZIONE],
    group: 'Back-office',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuova scadenza fiscale',
    paletteSub: 'F24, IVA, INPS, rate',
  },
  {
    key: 'attach-subscription',
    title: 'Abbonamento',
    sub: 'Checkout Stripe per il cliente',
    icon: 'link',
    modal: 'attachSubscription',
    module: [ModuleKey.SERVICES],
    group: 'Back-office',
    paletteColor: 'var(--info)',
    paletteLabel: 'Nuovo abbonamento',
    paletteSub: 'Checkout Stripe per il cliente',
  },
  {
    key: 'new-vendor',
    title: 'Professionista',
    sub: 'Fornitore / freelance',
    icon: 'brief',
    modal: 'newVendor',
    module: [ModuleKey.SUPPLIERS],
    group: 'Back-office',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuovo professionista',
    paletteSub: 'Fornitore / freelance',
  },
  {
    key: 'new-vault',
    title: 'Credenziale',
    sub: 'Voce nel Password Vault',
    icon: 'lock',
    modal: 'newVault',
    module: [ModuleKey.VAULT],
    group: 'Back-office',
    paletteColor: 'var(--ink)',
    paletteLabel: 'Nuova credenziale',
    paletteSub: 'Voce nel Password Vault',
  },
];

/** Ordine dei gruppi nel NewMenu della Topbar. */
export const NEW_ACTION_GROUP_ORDER: NewActionGroup[] = ['Operativo', 'Documenti', 'Back-office'];

/**
 * Filtra le voci visibili dato un predicato di permesso (canWrite per modulo).
 * Una voce è visibile se l'utente può scrivere su almeno uno dei suoi moduli.
 * FASE 0: i moduli in pausa (lib/frozen.ts) non contano — una voce i cui
 * moduli sono tutti congelati sparisce da "+ Nuovo" e dalla palette.
 */
export function visibleNewActions(
  actions: NewAction[],
  canWrite: (module: ModuleKey) => boolean,
): NewAction[] {
  return actions.filter((a) => a.module.some((m) => !isFrozen(m) && canWrite(m)));
}
